import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import {
    CATEGORY_LABELS,
    CONDITION_LABELS,
    OPEN_STATUSES,
    STATUS_LABELS,
    STATUS_VARIANTS,
} from '@/shared/constants/listings';
import { expiryWords } from '@/shared/utils/listingDates';
import '../listings.css';

// The card title is a heading, so its level has to follow the page it sits
// on: h2 under a page h1, h3 under a section h2. Fixing it here would skip a
// level on the grouped pages and fail the heading-order check.
function ListingCard({ listing, titleAs = 'h2', footer = null }) {
    const photo = (listing.photoUrls || [])[0];
    const categoryLabel = CATEGORY_LABELS[listing.category] || listing.category;
    // A deadline belongs to an item still on offer. A closed one carries a
    // stored expiry that means nothing to the reader, and its status badge
    // already says where it stands.
    const expiry = OPEN_STATUSES.includes(listing.status)
        ? expiryWords(listing.expiresAt)
        : '';
    return (
        <Card className="h-100">
            {photo ? (
                <Card.Img
                    variant="top"
                    src={photo}
                    alt={listing.title}
                    className="afe-card-photo"
                />
            ) : (
                <div className="afe-photo-placeholder">{categoryLabel}</div>
            )}
            <Card.Body>
                <Card.Title as={titleAs} className="h5">
                    <Link to={`/listings/${listing._id}`}>{listing.title}</Link>
                </Card.Title>
                <div className="mb-2">
                    <InstitutionLogo
                        institution={listing.institution}
                        className="me-1"
                    />
                    <Badge bg={STATUS_VARIANTS[listing.status]}>
                        {STATUS_LABELS[listing.status] || listing.status}
                    </Badge>
                </div>
                <Card.Text className="mb-1">
                    {categoryLabel}
                    {' · '}
                    {CONDITION_LABELS[listing.condition] || listing.condition}
                </Card.Text>
                <Card.Text className="text-muted mb-0">
                    {listing.building}
                </Card.Text>
                {listing.quantity > 1 && (
                    <Card.Text className="text-muted mb-0">
                        Quantity: {listing.quantity}
                    </Card.Text>
                )}
                {expiry && (
                    <Card.Text className="text-muted mb-0">
                        <time dateTime={listing.expiresAt}>{expiry}</time>
                    </Card.Text>
                )}
            </Card.Body>
            {/* Actions belong inside the card: a card fills its grid cell,
                so anything placed after it in the cell overflows into the
                row below. */}
            {footer && (
                <Card.Footer className="bg-white afe-actions">
                    {footer}
                </Card.Footer>
            )}
        </Card>
    );
}

ListingCard.propTypes = {
    listing: PropTypes.object.isRequired,
    titleAs: PropTypes.string,
    footer: PropTypes.node,
};

export default ListingCard;
